import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AJHMLogo from '../components/AJHMLogo';
import { 
  FaSearch, 
  FaUpload, 
  FaCheckCircle, 
  FaClock, 
  FaArrowRight, 
  FaEnvelope 
} from 'react-icons/fa';

const Tracking = () => {
  const [trackingId, setTrackingId] = useState('');
  const [articleStatus, setArticleStatus] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearched(true);
    // Simulated status for testing/demo
    setArticleStatus({
      id: trackingId.trim().toUpperCase(),
      title: "Evaluation of Homoeopathic Dilutions in Clinical Practice: A Multi-Center Observational Study",
      status: "In Peer Review",
      lastUpdated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      currentStage: "Peer Review Evaluation",
      nextStage: "Editorial Decision & Revisions",
      estimatedCompletion: "Within 2–3 weeks",
      step: 3
    });
  };

  const stages = [
    { num: 1, name: "Manuscript Received", desc: "Submitted & files verified" },
    { num: 2, name: "Initial Editorial Screen", desc: "Scope check & plagiarism check" },
    { num: 3, name: "Double-Blind Peer Review", desc: "Under review by external referees" },
    { num: 4, name: "Editorial Decision", desc: "Revision or acceptance" },
    { num: 5, name: "Publication & DOI", desc: "Copyediting & online archiving" },
  ];

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">Track Manuscript</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        
        {/* Header Block matching the new academic UI standard */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-2">
          <div>
            <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
              MANUSCRIPT STATUS PORTAL
            </span>
            <h1 className="page-title">
              Track Your Submission
            </h1>
            <p className="text-base text-gray-600 font-medium mt-1">
              Check real-time peer review status and editorial milestones for your submitted paper
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/upload"
              className="inline-flex items-center gap-2 text-xs font-bold text-white bg-[#03478E] hover:bg-[#023366] px-5 py-2.5 rounded-full transition-all duration-200 shadow-2xs uppercase tracking-wider"
            >
              <FaUpload className="text-xs" />
              <span>Submit New Manuscript</span>
            </Link>
          </div>
        </div>

        {/* 12-Column Grid: Tracking Search & Results (8 cols) & Sidebar (4 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          
          {/* Main Search & Status Column (8 cols) */}
          <main className="lg:col-span-8 bg-white rounded-3xl p-8 sm:p-12 shadow-sm space-y-8">
            
            {/* Search Input Box */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                Enter Manuscript Tracking ID / Reference Number
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    required
                    placeholder="e.g. AJHM-2025-0012"
                    className="w-full text-xs sm:text-sm pl-11 pr-4 py-3.5 rounded-2xl bg-[#F8FAFC] border-none focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 text-gray-800 transition uppercase tracking-wider font-semibold"
                  />
                  <FaSearch className="absolute left-4 top-4 text-gray-400 text-sm" />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#03478E] hover:bg-[#023366] text-white text-xs font-bold rounded-full transition-all duration-200 shadow-md uppercase tracking-wider flex-shrink-0"
                >
                  <span>Track Manuscript</span>
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
              <p className="text-xs text-gray-400">
                Your tracking ID was sent to the corresponding author email upon manuscript submission.
              </p>
            </form>

            {/* Results Display */}
            {searched && articleStatus && (
              <div className="pt-6 border-t border-gray-100 space-y-8 animate-fadein">
                
                {/* Status Summary Banner */}
                <div className="bg-blue-50/60 p-6 rounded-3xl space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                      TRACKING ID: {articleStatus.id}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-800 bg-blue-100/80 px-3 py-1 rounded-full self-start sm:self-auto">
                      <FaClock className="text-[10px]" /> {articleStatus.status}
                    </span>
                  </div>

                  <h3 className="text-base font-bold text-default leading-snug">
                    {articleStatus.title}
                  </h3>

                  <div className="flex flex-wrap gap-4 text-xs text-gray-600 pt-1">
                    <div>Last Updated: <strong className="text-gray-800">{articleStatus.lastUpdated}</strong></div>
                    <div>Expected Turnaround: <strong className="text-gray-800">{articleStatus.estimatedCompletion}</strong></div>
                  </div>
                </div>

                {/* Progress Stepper */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                    Review Milestones
                  </h4>
                  <div className="space-y-3">
                    {stages.map((stage) => {
                      const isCompleted = stage.num < articleStatus.step;
                      const isCurrent = stage.num === articleStatus.step;
                      return (
                        <div
                          key={stage.num}
                          className={`p-4 rounded-2xl flex items-start gap-4 transition ${
                            isCurrent
                              ? 'bg-blue-50/80 border-l-4 border-[#03478E]'
                              : isCompleted
                              ? 'bg-emerald-50/50'
                              : 'bg-gray-50/50 opacity-60'
                          }`}
                        >
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              isCompleted
                                ? 'bg-emerald-500 text-white'
                                : isCurrent
                                ? 'bg-[#03478E] text-white shadow-xs'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {isCompleted ? <FaCheckCircle className="text-sm" /> : stage.num}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h5 className="text-xs sm:text-sm font-bold text-gray-900">
                                {stage.name}
                              </h5>
                              {isCurrent && (
                                <span className="text-xs font-bold text-[#03478E] uppercase tracking-wider bg-white px-2 py-0.5 rounded-full shadow-2xs">
                                  Current Stage
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{stage.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
            )}

          </main>

          {/* Right Sidebar Details Column (4 cols) */}
          <aside className="lg:col-span-4 space-y-6">
            
            {/* Journal Info Card */}
            <div className="bg-white rounded-3xl p-7 shadow-sm text-center">
              <div className="flex justify-center mb-5">
                <AJHMLogo />
              </div>

              <div className="py-4 my-2 space-y-3.5 text-left text-xs sm:text-sm bg-gray-50/70 p-5 rounded-2xl">
                <div>
                  <span className="text-gray-500 block text-xs uppercase tracking-wider font-semibold">
                    Review Process
                  </span>
                  <span className="font-bold text-gray-800">
                    Double-Blind Peer Review
                  </span>
                </div>

                <div>
                  <span className="text-gray-500 block text-xs uppercase tracking-wider font-semibold">
                    Editorial Decisions
                  </span>
                  <span className="font-bold text-gray-800">
                    Communicated via Email
                  </span>
                </div>

                <div>
                  <span className="text-gray-500 block text-xs uppercase tracking-wider font-semibold">
                    Average Initial Decision
                  </span>
                  <span className="font-bold text-gray-800">
                    21 – 28 Days
                  </span>
                </div>
              </div>

              <div className="pt-3">
                <Link
                  to="/upload"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#03478E] hover:bg-[#023366] text-white hover:text-white text-xs font-bold rounded-full transition uppercase tracking-wider shadow-sm"
                >
                  <FaUpload className="text-xs" />
                  <span>Submit Manuscript</span>
                </Link>
              </div>
            </div>

            {/* Assistance Card */}
            <div className="bg-white rounded-3xl p-6 shadow-sm space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                Lost Your Tracking ID?
              </h4>
              <p className="text-xs text-gray-600 leading-relaxed font-sans">
                Contact the editorial office with your manuscript title, corresponding author name, and date of submission:
              </p>
              <a
                href="mailto:editor@aimjhm.com"
                className="inline-flex items-center gap-2 text-xs font-bold text-[#03478E] hover:underline"
              >
                <FaEnvelope className="text-[10px]" />
                <span>editor@aimjhm.com</span>
              </a>
              <a
                href="mailto:domus.publications@gmail.com"
                className="flex items-center gap-2 text-xs font-bold text-[#03478E] hover:underline"
              >
                <FaEnvelope className="text-[10px]" />
                <span>domus.publications@gmail.com</span>
              </a>
            </div>

          </aside>

        </div>
      </div>
    </div>
  );
};

export default Tracking;